var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Utils, PCSS, P, Transformer, ViewChild, Components, CustomElement, Watch, PropertyConverters, PropertyChangeEventName, Debounce, UI } from '@pacem/pacem-core';
import { Components as ComponentsUI } from '@pacem/pacem-ui';
class Transformers {
    static removeLeadingTabs(html) {
        var min = Number.MAX_SAFE_INTEGER;
        var split = html.split('\n');
        for (var line of split) {
            let match = /^(\s*)[^\s]+/.exec(line);
            if (match)
                min = Math.min(match[1].length, min);
        }
        var retval = [];
        const regex = new RegExp(`^\\s{${min}}`, 'gm');
        for (var line of split) {
            retval.push(line.replace(regex, ''));
        }
        return retval.join('\n').replace(/^\s+|\s+$/g, '');
    }
    static snippetSource(html) {
        const unescaped = unescapeAttributeValues(html ?? '');
        return Transformers.removeLeadingTabs(unescaped);
    }
}
__decorate([
    Transformer()
], Transformers, "removeLeadingTabs", null);
__decorate([
    Transformer()
], Transformers, "snippetSource", null);
function unescapeAttributeValues(html) {
    return html.replace(/="([^"]*)"/g, (_, value) => {
        const unescaped = value
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&');
        return `="${unescaped}"`;
    });
}
var COPY_TOASTER_SINGLETON;
var COPY_TOAST_SINGLETON;
const EDIT_RENDER_DELAY = 400;
const PLAY_DEMO_MIN_HEIGHT = 48;
const PLAY_CODE_MIN_HEIGHT = 96;
const PLAYING_CLASS = 'snippet-playing';
const PLAY_CONTENT = `<div class="snippet-play">
    <div class="snippet-play-demo"><div class="snippet-play-scroller"><${P}-view></${P}-view></div></div>
    <div class="snippet-play-code">
        <${P}-input-code wrap="true" class="field-stretch"></${P}-input-code>
        <${P}-button class="snippet-play-reset button-circular circle-small" icon-glyph="undo" tooltip="restore the original code" aria-label="reset"></${P}-button>
    </div>
</div>`;
let PacemSnippetElement = class PacemSnippetElement extends Components.PacemElement {
    constructor() {
        super(...arguments);
        this.#playing = false;
        this._tabsChangeHandler = (evt) => {
            if (evt.detail.propertyName !== 'index' || Utils.isNull(this.#playTab)) {
                return;
            }
            const items = this._tabs.items ?? [];
            const playIndex = items.indexOf(this.#playTab);
            this._setPlaying(evt.detail.currentValue === playIndex);
        };
        this._resetHandler = () => {
            this.#editor.value = this._originalCode;
        };
        this._editorChangeHandler = (evt) => {
            if (evt.detail.propertyName !== 'value') {
                return;
            }
            this._renderCode(evt.detail.currentValue);
        };
        this._rescaleStartHandler = () => {
            const demo = this.#playDemo;
            const code = demo.nextElementSibling;
            const gap = code.offsetTop - (demo.offsetTop + demo.offsetHeight);
            this._rescale.maxHeight = demo.parentElement.clientHeight - gap - PLAY_CODE_MIN_HEIGHT;
        };
        this._rescaleHandler = (evt) => {
            evt.preventDefault();
            const available = this.#playDemo.parentElement.clientHeight;
            if (available <= 0) {
                return;
            }
            const height = evt.detail.targetRect.height;
            const share = 100 * height / available;
            this.#playDemo.style.height = `${share}%`;
        };
    }
    #playTab;
    #playView;
    #playDemo;
    #editor;
    #resetButton;
    #playing;
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        if (name === 'editable') {
            this._applyEditable();
        }
    }
    viewActivatedCallback() {
        super.viewActivatedCallback();
        this._itemTemplate = this.querySelector('template');
        var id = this._itemTemplate.id;
        if (Utils.isNullOrEmpty(id)) {
            id = this._itemTemplate.id = `_${Utils.uniqueCode()}`;
        }
        this._view.setAttribute('url', `#${id}`);
        this._md.setAttribute('value', `{{ '\`\`\`html\\n\\n'+ $pacem.snippetSource(#${id}.innerHTML) +'\\n\`\`\`' }}`);
        this._originalCode = this._currentCode = Transformers.snippetSource(this._itemTemplate.innerHTML);
        this._currentTemplate = this._itemTemplate;
        this._tabs.addEventListener(PropertyChangeEventName, this._tabsChangeHandler, false);
        this._rescale.addEventListener(UI.RescaleEventType.Start, this._rescaleStartHandler, false);
        this._rescale.addEventListener(UI.RescaleEventType.Rescale, this._rescaleHandler, false);
        this._applyEditable();
        this._setupToaster();
    }
    disconnectedCallback() {
        this._tabs?.removeEventListener(PropertyChangeEventName, this._tabsChangeHandler, false);
        this._rescale?.removeEventListener(UI.RescaleEventType.Start, this._rescaleStartHandler, false);
        this._rescale?.removeEventListener(UI.RescaleEventType.Rescale, this._rescaleHandler, false);
        super.disconnectedCallback();
    }
    _applyEditable() {
        if (Utils.isNull(this._tabs)) {
            return;
        }
        const hasPlayTab = !Utils.isNull(this.#playTab);
        if (this.editable && !hasPlayTab) {
            this._createPlayTab();
            return;
        }
        if (!this.editable && hasPlayTab) {
            this._removePlayTab();
        }
    }
    _createPlayTab() {
        const tab = this.#playTab = document.createElement(P + '-tab');
        tab.setAttribute('label', 'play');
        tab.className = 'snippet-play-tab';
        tab.innerHTML = PLAY_CONTENT;
        const codeTab = this._md.closest(P + '-tab');
        codeTab.after(tab);
        this.#playView = tab.querySelector(P + '-view');
        this.#playDemo = tab.querySelector('.snippet-play-demo');
        this.#editor = tab.querySelector(P + '-input-code');
        this.#resetButton = tab.querySelector('.snippet-play-reset');
        this.#editor.addEventListener(PropertyChangeEventName, this._editorChangeHandler, false);
        this.#resetButton.addEventListener('click', this._resetHandler, false);
        this._rescale.register(this.#playDemo);
    }
    _removePlayTab() {
        this._setPlaying(false);
        this.#editor.removeEventListener(PropertyChangeEventName, this._editorChangeHandler, false);
        this.#resetButton.removeEventListener('click', this._resetHandler, false);
        this._rescale.unregister(this.#playDemo);
        this.#playTab.remove();
        this.#playTab = this.#playView = this.#playDemo = this.#editor = this.#resetButton = null;
    }
    _setPlaying(playing) {
        if (this.#playing === playing) {
            return;
        }
        this.#playing = playing;
        (playing ? Utils.addClass : Utils.removeClass)(this, PLAYING_CLASS);
        if (!playing) {
            this.#playView.url = null;
            this._view.url = this._currentTemplate;
            return;
        }
        this._view.url = null;
        if (Utils.isNull(this.#editor.value)) {
            this.#editor.value = this._currentCode;
        }
        this.#playView.url = this._currentTemplate;
        this._tabs.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
    _renderCode(code) {
        const source = code ?? '';
        if (source === this._currentCode) {
            return;
        }
        this._currentCode = source;
        const template = this._currentTemplate = document.createElement('template');
        template.innerHTML = source;
        const view = this.#playing ? this.#playView : this._view;
        view.url = template;
        this._md.value = '```html\n\n' + source + '\n```';
    }
    _setupToaster() {
        var toaster = COPY_TOASTER_SINGLETON;
        if (Utils.isNull(toaster)) {
            toaster = COPY_TOASTER_SINGLETON = document.createElement('div');
            Utils.addClass(toaster, PCSS + '-toaster toaster-bottom');
            var toast = COPY_TOAST_SINGLETON = new ComponentsUI.UI.PacemToastElement();
            toast.timeout = 1250;
            Utils.addClass(toast, 'toast-primary');
            toast.textContent = "Copied!";
            toaster.appendChild(toast);
            document.body.appendChild(toaster);
        }
    }
    _copy() {
        Utils.copyToClipboard(this._currentCode).then(_ => {
            if (!Utils.isNull(COPY_TOAST_SINGLETON))
                COPY_TOAST_SINGLETON.show = true;
        });
    }
};
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Boolean })
], PacemSnippetElement.prototype, "editable", void 0);
__decorate([
    ViewChild(P + '-tabs')
], PacemSnippetElement.prototype, "_tabs", void 0);
__decorate([
    ViewChild(P + '-view')
], PacemSnippetElement.prototype, "_view", void 0);
__decorate([
    ViewChild(P + '-markdown')
], PacemSnippetElement.prototype, "_md", void 0);
__decorate([
    ViewChild(P + '-rescale')
], PacemSnippetElement.prototype, "_rescale", void 0);
__decorate([
    Debounce(EDIT_RENDER_DELAY)
], PacemSnippetElement.prototype, "_renderCode", null);
PacemSnippetElement = __decorate([
    CustomElement({
        tagName: 'pacemjs-snippet', shadow: false,
        template: `<${P}-content></${P}-content>
    <h4 class="snippet-header">&lt;Snippet/&gt;</h4>
    <${P}-tabs class="tabs-rise">
    <${P}-tab label="demo">
        <${P}-view></${P}-view>
    </${P}-tab>
    <${P}-tab label="code" class="pos-relative">
        <${P}-markdown class="text-small"></${P}-markdown>
        <${P}-button class="hide-md flat circular copy pos-absolute absolute-top absolute-right" on-click=":host._copy()" tooltip="copy code snippet"></${P}-button>
    </${P}-tab>
</${P}-tabs>
    <${P}-rescale handles="bottom" min-height="${PLAY_DEMO_MIN_HEIGHT}"></${P}-rescale>
    <div class="${PCSS}-toaster toaster-bottom">
        <${P}-toast timeout="1250" class="toast-primary"><span>Copied!</span></${P}-toast>
    </div>`
    })
], PacemSnippetElement);
export { PacemSnippetElement };
